import type { ReactNode } from "react";
import { useState } from "react";
import { cn } from "@/shared/lib/utils";
import {
  COLOR_BORDER_SUBTLE,
  OVERLAY_HOVER,
  OVERLAY_REST,
  SHADOW_CARD,
  SHADOW_CARD_HOVER,
} from "../../constants/upsellPage.constants";
import { useRevealOnScroll } from "../../hooks/useRevealOnScroll";

type MediaCardProps = {
  backgroundUrl: string
  media: ReactNode
  body: ReactNode
  revealDelay?: number
  mediaClassName?: string
  bodyClassName?: string
};

const MediaCard = ({
  backgroundUrl,
  media,
  body,
  revealDelay = 0,
  mediaClassName,
  bodyClassName,
}: MediaCardProps) => {
  const { ref, isVisible } = useRevealOnScroll<HTMLDivElement>();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      ref={ref}
      className={cn(
        "group relative rounded-3xl bg-white p-1.5 transition-all duration-300 ease-out hover:-translate-y-0.5",
        isVisible ? "animate-upsell-fade-in-up" : "opacity-0",
      )}
      style={{
        border: `1px solid ${COLOR_BORDER_SUBTLE}`,
        boxShadow: isHovered ? SHADOW_CARD_HOVER : SHADOW_CARD,
        animationDelay: isVisible ? `${revealDelay * 0.06}s` : undefined,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="overflow-hidden rounded-[1.25rem] bg-white">
        <div
          className={cn(
            "relative flex h-32 items-center justify-center bg-cover bg-center",
            mediaClassName,
          )}
          style={{ backgroundImage: `url(${backgroundUrl})` }}
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 transition-colors duration-300"
            style={{ backgroundColor: isHovered ? OVERLAY_HOVER : OVERLAY_REST }}
          />
          <div className="relative z-10 flex items-center justify-center">
            {media}
          </div>
        </div>
        <div
          className={cn(
            "flex flex-col items-center px-6 py-6 text-center",
            bodyClassName,
          )}
        >
          {body}
        </div>
      </div>
    </div>
  );
};

export default MediaCard;
